import { useEffect, useMemo, useRef, useState } from 'react';
import { Crop, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { Button, Modal, Note } from '@/components/ui';

/**
 * قصُّ الصورة قبل رفعها (24 سبتمبر 2026) — بلا مكتبة: لوحةٌ (canvas) تُرسم عليها الصورة داخل إطارٍ
 * بنسبة الوجهة، ويُحرّكها المستخدم بالسحب ويكبّرها بالمزلاج، ثم تُقصّ إلى الأبعاد التي تُحفظ فعلاً.
 *
 * ما يراه قبل أن يضغط «اقصّ»:
 *   · **أبعادُ الأصل** كما رُفع، و**أبعادُ ما سيُحفظ** (مثلاً 512×512)، و**أين تُعرض وبأي حجم** — فلا
 *     يُفاجَأ بأن صورته العريضة صارت دائرةً تقطع رأسه في الشريط الجانبي.
 *   · الإطارُ بشكل الوجهة نفسِه: دائرةٌ للوجوه (تُحفظ مربّعاً وتُعرض دائرة)، ومستطيلٌ للأخبار والألبوم.
 * والصورةُ لا تغادر المتصفّح حتى تُقصّ: ما يُرفع هو الناتج وحده — أصغرُ وأخفّ وبالشكل الذي طُلب.
 */
export type { CropSpec } from './crop-specs';
import type { CropSpec } from './crop-specs';

const VIEW = 360;

export function ImageCropDialog({ file, spec, onDone, onCancel }: { file: File; spec: CropSpec; onDone: (cropped: File) => void; onCancel: () => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  const frame = useMemo(() => {
    const w = spec.aspect >= 1 ? VIEW : Math.round(VIEW * spec.aspect);
    const h = spec.aspect >= 1 ? Math.round(VIEW / spec.aspect) : VIEW;
    return { w, h };
  }, [spec.aspect]);
  const outH = Math.round(spec.outputWidth / spec.aspect);

  // الصورةُ تُحمَّل من الملف في المتصفّح، والرابطُ يُبطل بعد التحميل
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { setImg(im); URL.revokeObjectURL(url); };
    im.onerror = () => { setError('تعذّر قراءة الصورة — جرّب ملفاً آخر.'); URL.revokeObjectURL(url); };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // أصغرُ تكبيرٍ يملأ الإطارَ كلَّه (لا فراغَ في الناتج)، والموضعُ يُقيَّد داخله
  const base = img ? Math.max(frame.w / img.naturalWidth, frame.h / img.naturalHeight) : 1;
  const scale = base * zoom;
  const clamp = (p: { x: number; y: number }) => {
    if (!img) return p;
    const dw = img.naturalWidth * scale; const dh = img.naturalHeight * scale;
    const maxX = Math.max(0, (dw - frame.w) / 2); const maxY = Math.max(0, (dh - frame.h) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  };
  const p = clamp(pos);

  useEffect(() => {
    const c = canvas.current; if (!c || !img) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    c.width = frame.w; c.height = frame.h;
    ctx.clearRect(0, 0, frame.w, frame.h);
    const dw = img.naturalWidth * scale; const dh = img.naturalHeight * scale;
    ctx.drawImage(img, (frame.w - dw) / 2 + p.x, (frame.h - dh) / 2 + p.y, dw, dh);
    // ما خارج الدائرة يُعتَّم ليُرى ما سيُقطع
    if (spec.round) {
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,.45)';
      ctx.beginPath(); ctx.rect(0, 0, frame.w, frame.h);
      ctx.arc(frame.w / 2, frame.h / 2, Math.min(frame.w, frame.h) / 2, 0, Math.PI * 2, true);
      ctx.fill(); ctx.restore();
    }
  }, [img, scale, p.x, p.y, frame.w, frame.h, spec.round]);

  const onDown = (e: React.PointerEvent) => { drag.current = { x: e.clientX, y: e.clientY, px: p.x, py: p.y }; (e.target as Element).setPointerCapture(e.pointerId); };
  const onMove = (e: React.PointerEvent) => { if (!drag.current) return; setPos(clamp({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) })); };
  const onUp = () => { drag.current = null; };

  function finish() {
    if (!img) return;
    const out = document.createElement('canvas');
    out.width = spec.outputWidth; out.height = outH;
    const ctx = out.getContext('2d'); if (!ctx) return;
    // الإطارُ في اللوحة ⇒ منطقةٌ في الأصل: تُنقل بالمقياس نفسِه إلى أبعاد الناتج
    const dw = img.naturalWidth * scale; const dh = img.naturalHeight * scale;
    const left = (frame.w - dw) / 2 + p.x; const top = (frame.h - dh) / 2 + p.y;
    const sx = -left / scale; const sy = -top / scale;
    const sw = frame.w / scale; const sh = frame.h / scale;
    const mime = spec.mime ?? 'image/jpeg';
    if (mime === 'image/jpeg') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, out.width, out.height); }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, out.width, out.height);
    out.toBlob((blob) => {
      if (!blob) { setError('تعذّر قصّ الصورة.'); return; }
      const ext = mime === 'image/png' ? 'png' : 'jpg';
      onDone(new File([blob], file.name.replace(/\.[^.]+$/, '') + `-cropped.${ext}`, { type: mime }));
    }, mime, 0.9);
  }

  const tooSmall = !!img && (img.naturalWidth < spec.outputWidth * 0.5 || img.naturalHeight < outH * 0.5);
  return (
    <Modal title="اضبط الصورة قبل رفعها" onClose={onCancel}
      description="اسحب الصورة داخل الإطار وكبّرها أو صغّرها — ما داخل الإطار هو ما يُحفظ."
      footer={(
        <>
          <Button variant="primary" icon={<Crop size={16} />} onClick={finish} disabled={!img} disabledReason={!img ? 'جارٍ تحميل الصورة…' : undefined}>اقصّ واستعمل</Button>
          <Button variant="ghost" onClick={onCancel}>إلغاء</Button>
        </>
      )}>
      <div className="stack gap-3">
        {error && <Note kind="danger">{error}</Note>}
        <div className="crop-stage" style={{ inlineSize: frame.w, blockSize: frame.h }}>
          <canvas ref={canvas} className={`crop-canvas${spec.round ? ' crop-canvas--round' : ''}`}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            role="img" aria-label="معاينة القصّ — اسحب لتحريك الصورة" />
        </div>
        <div className="row gap-2 wrap crop-controls">
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setZoom((z) => Math.max(1, +(z - 0.1).toFixed(2)))} aria-label="تصغير"><ZoomOut size={16} /></button>
          <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} aria-label="التكبير" className="crop-zoom" />
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setZoom((z) => Math.min(4, +(z + 0.1).toFixed(2)))} aria-label="تكبير"><ZoomIn size={16} /></button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setZoom(1); setPos({ x: 0, y: 0 }); }}><RotateCcw size={14} aria-hidden /> إعادة</button>
        </div>
        <dl className="crop-facts">
          <div><dt>الأصل</dt><dd><bdi dir="ltr">{img ? `${img.naturalWidth} × ${img.naturalHeight}` : '…'}</bdi> بكسل</dd></div>
          <div><dt>ما يُحفظ</dt><dd><bdi dir="ltr">{spec.outputWidth} × {outH}</bdi> بكسل{spec.round ? ' — يُعرض دائرة' : ''}</dd></div>
          <div><dt>أين تظهر</dt><dd>{spec.displays.join(' · ')}</dd></div>
        </dl>
        {tooSmall && <Note kind="warning">الصورة أصغر من نصف أبعاد الحفظ — ستظهر باهتة. اختر صورةً أكبر إن استطعت.</Note>}
      </div>
    </Modal>
  );
}
